import { createClient } from '@supabase/supabase-js'

// ─── Runtime env routing ─────────────────────────────────────────────────
//
// Every build bakes its own `VITE_SUPABASE_URL` / `_ANON_KEY` (prod for
// `npm run build`, staging for `--mode staging`, etc.). For the App Store
// review build we ALSO bake the staging URL + anon key as hardcoded
// constants below, so that a tiny allowlist of emails (see AuthContext)
// can sign in against staging from inside a prod-pointing binary.
//
// The override is a synchronous localStorage flag (`teesync_env_override`).
// When it equals `'staging'`, the module-level client points at the
// staging project; otherwise it falls back to the build-time env vars.
//
// Anon JWTs are public by design - safe to commit. The service-role key
// is NEVER baked into the client bundle.
// ────────────────────────────────────────────────────────────────────────

export const STAGING = {
  url: 'https://iwyqmtttfdiudvnuavtm.supabase.co',
  anonKey:
    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Iml3eXFtdHR0ZmRpdWR2bnVhdnRtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgwNTczMjcsImV4cCI6MjA5MzYzMzMyN30.Cw1IlocAuarigK2p3XEew74tzNrLPEaTtY5tWwtfGFE',
}

export const PROD = {
  url: import.meta.env.VITE_SUPABASE_URL,
  anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
}

const OVERRIDE_KEY = 'teesync_env_override'

function readOverride() {
  if (typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem(OVERRIDE_KEY) === 'staging'
  } catch {
    return false
  }
}

const _overrideAtBoot = readOverride()
const _active = _overrideAtBoot ? STAGING : PROD

if (!_active.url || !_active.anonKey) {
  throw new Error(
    'Missing Supabase credentials. Build-time env vars (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY) are required for the default (prod) path.'
  )
}

export const supabase = createClient(_active.url, _active.anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
})

// ─── Helpers for callers that build their own URLs (OAuth callbacks,
// edge function fetches, admin tools, PostHog env labelling) ────────────

export const getActiveSupabaseUrl = () => _active.url
export const getActiveAnonKey = () => _active.anonKey
export const isStagingOverride = () => _overrideAtBoot

export function setStagingOverride(on) {
  if (typeof localStorage === 'undefined') return
  try {
    if (on) localStorage.setItem(OVERRIDE_KEY, 'staging')
    else localStorage.removeItem(OVERRIDE_KEY)
  } catch {
    /* no-op - Safari private mode etc. */
  }
}

export default supabase
